import z from "zod";

import { NodeRole } from "@skilitics/nautilus-core/schemas";

import { multilingualNodeConfigSchema, type MultilingualNodeConfig, type NodeConfig } from "../shared/index.ts";
import type { TriggerDesign } from "./triggerDesignSchema.ts";
import { triggerDesignSchema } from "./triggerDesignSchema.ts";

export type DesignerNodeData<TConfig extends Record<string, unknown> = MultilingualNodeConfig> = {
  id: number;
  typeId: string;
  config: TConfig;
  version: string;
  childNodes: Record<string, DesignerNodeData | DesignerNodeData[] | undefined>;
  placeholder?: DesignerNodeData;
  isPlaceholder?: boolean;
  role?: NodeRole;
  triggers?: TriggerDesign[];
  upstreamTemplateId?: number;
  owner?: OwnerOrCreator;
  creator?: OwnerOrCreator;
};

export type DesignerNodeDataMonolingual<TConfig extends Record<string, unknown> = NodeConfig> = Omit<
  DesignerNodeData<TConfig>,
  "config" | "childNodes"
> & {
  config: TConfig;
  childNodes: Record<string, DesignerNodeDataMonolingual | DesignerNodeDataMonolingual[] | undefined>;
};

export const ownerOrCreatorSchema = z.enum(["ai", "user"]);

export type OwnerOrCreator = z.infer<typeof ownerOrCreatorSchema>;

export const designerNodeDataSchema: z.ZodType<DesignerNodeData> = z.lazy(() =>
  z.object({
    id: z.number(),
    typeId: z.string().min(1),
    version: z.string().min(1),
    config: multilingualNodeConfigSchema,
    role: NodeRole.optional(),
    childNodes: z.record(z.union([designerNodeDataSchema, z.array(designerNodeDataSchema), z.undefined()])),
    placeholder: designerNodeDataSchema.optional(),
    triggers: z.array(triggerDesignSchema).optional(),
    upstreamTemplateId: z.number().int().optional(),
    isPlaceholder: z.boolean().optional(),
    owner: ownerOrCreatorSchema.optional(),
    creator: ownerOrCreatorSchema.optional(),
  }),
);
